import { TbBrandGmail, TbBrandSlack, TbCalendarEvent } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { type Scene, useScene, useTyping } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import {
  ChiefPane,
  DoneRow,
  EndedBar,
  GHOST_BUTTON,
  OpenDocument,
  PaneSection,
  SessionPill,
  SessionWindow,
  startedLabel,
  Swap,
  type TranscriptEntry,
  TranscriptBlock,
  WorkingRow,
} from "./internal/chief";
import { NotesPage, SelectedNotes } from "./internal/notes";
import { at, MockScene } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

// The follow-up destinations' marks, as the Activity panel draws them.
const PROVIDERS = { gmail: TbBrandGmail, slack: TbBrandSlack, calendar: TbCalendarEvent };

export type FollowThroughData = {
  project: string;
  session: string;
  started: string;
  duration: string;
  /**
   * The end of the call, played first: `said` is already on screen, `promise` types in as it is
   * spoken, and Chief drafts the follow-up it promises into Gmail before the call ends.
   */
  call: {
    clock: string;
    said: TranscriptEntry;
    promise: TranscriptEntry;
    drafting: string;
    email: { subject: string; body: string };
    gmail: string;
    send: string;
  };
  /** The points selected in the notes, and the document kinds they can be turned into; `picked` is the one chosen. */
  selected: string[];
  formats: string[];
  picked: number;
  draft: { title: string; lines: string[] };
  /** The work accepted during the call, finishing in turn. */
  followUps: { provider: keyof typeof PROVIDERS; title: string; adding: string; done: string }[];
};

// Seconds into the scene for the call. Drafting starts while the promise is still typing in.
const CALL = { typeFrom: 0.6, drafting: 3.4, email: 5.6, gmail: 7.2, end: 10 };

// Seconds into the scene after the call. Each cursor glides for about 1.1s before it clicks.
const T = {
  turnCursor: CALL.end + 0.2,
  turn: CALL.end + 1.3,
  menu: CALL.end + 1.5,
  pickCursor: CALL.end + 1.6,
  pick: CALL.end + 2.7,
  draft: CALL.end + 2.9,
  line: CALL.end + 3.2,
  firstDone: CALL.end + 2.2,
  perDone: 1.4,
};

const doneAt = (i: number) => T.firstDone + i * T.perDone;

/** A count that moves to the next of `values` at each of `at`. */
const Count: Component<{ values: number[]; at: number[] }> = (props) => (
  <Show when={props.at.length > 0} fallback={props.values[0]}>
    <Swap
      swapAt={props.at[0]}
      before={props.values[0]}
      after={<Count values={props.values.slice(1)} at={props.at.slice(1)} />}
    />
  </Show>
);

/**
 * The end of the call and the Live Session after it, played once: Chief drafts
 * the follow-up promised on the call into Gmail as it is said; then the points
 * selected in Notes are turned into a brief that opens in Documents, while the
 * follow-ups accepted during the call finish one after another in Activity.
 */
export const FollowThroughMockup: Component<{ data: FollowThroughData; class?: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  return (
    <MockScene scene={scene} label={t("marketing.mock.follow.alt")} class={props.class}>
      <ScaledFrame chrome="app" width={960} height={540} narrow={{ width: 400, height: 820 }}>
        <SessionWindow
          project={props.data.project}
          title={props.data.session}
          facts={
            <Swap
              swapAt={CALL.end}
              before={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
              after={`${t("marketing.mock.summary.ended")} · ${props.data.duration}`}
            />
          }
          liveUntil={CALL.end}
          tab="transcript"
          next={[
            { tab: "notes", at: CALL.end },
            { tab: "documents", at: T.draft },
          ]}
          bar={
            <Swap
              swapAt={CALL.end}
              before={<SessionPill clock={props.data.call.clock} wave={false} />}
              after={<EndedBar class="mac-pill pointer-events-auto border border-primary-border bg-tile shadow-2xl" />}
            />
          }
          pane={
            <ChiefPane class="h-65 shrink-0 @2xl:h-auto @2xl:w-75">
              <CallDraft call={props.data.call} />
              <div class="card-appear p-1.5" style={at(CALL.end)}>
                <div
                  class="collapse-out grid grid-rows-[0fr] opacity-0"
                  style={at(doneAt(props.data.followUps.length - 1))}
                >
                  <div class="min-h-0 overflow-hidden">
                    <PaneSection
                      verb="waiting"
                      count={
                        <Count
                          values={props.data.followUps.map((_, i) => props.data.followUps.length - i)}
                          at={props.data.followUps.slice(1).map((_, i) => doneAt(i))}
                        />
                      }
                    />
                    <For each={props.data.followUps}>
                      {(item, i) => (
                        <div class="collapse-out grid grid-rows-[0fr] opacity-0" style={at(doneAt(i()))}>
                          <div class="min-h-0 overflow-hidden">
                            <WorkingRow
                              title={item.title}
                              status={
                                <>
                                  <Dynamic component={PROVIDERS[item.provider]} class="size-3" aria-hidden="true" />
                                  {item.adding}
                                </>
                              }
                            />
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
                <div class="card-appear" style={at(doneAt(0))}>
                  <PaneSection
                    verb="did"
                    count={
                      <Count
                        values={props.data.followUps.map((_, i) => i + 1)}
                        at={props.data.followUps.slice(1).map((_, i) => doneAt(i + 1))}
                      />
                    }
                  />
                  <For each={props.data.followUps}>
                    {(item, i) => (
                      <div class="grow-in grid grid-rows-[1fr]" style={at(doneAt(i()))}>
                        <div class="min-h-0 overflow-hidden">
                          <DoneRow title={item.title} where={item.done} />
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </ChiefPane>
          }
        >
          <div class="grid h-full">
            <CallTranscript call={props.data.call} scene={scene} />
            <div class="card-appear col-start-1 row-start-1" style={at(CALL.end)}>
              <div class="exit" style={at(T.draft)}>
                <NotesPage
                  project={props.data.project}
                  title={props.data.session}
                  meta={startedLabel(props.data.started, currentLanguage())}
                  total={props.data.selected.length}
                  pills={[]}
                >
                  <SelectedNotes
                    class="mt-14"
                    lines={props.data.selected}
                    formats={props.data.formats}
                    picked={props.data.picked}
                    play={T}
                  />
                </NotesPage>
              </div>
            </div>
            <div class="card-appear col-start-1 row-start-1" style={at(T.draft)}>
              <OpenDocument>
                <div class="px-6 pt-4 @2xl:px-10">
                  <h1 class="mb-3 text-2xl leading-tight font-semibold">{props.data.draft.title}</h1>
                  <ul class="flex list-disc flex-col gap-1.5 ps-5 text-sm marker:text-muted-text">
                    <For each={props.data.draft.lines}>
                      {(line, i) => (
                        <li class="msg-in" style={at(T.line + i() * 0.3)}>
                          {line}
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </OpenDocument>
            </div>
          </div>
        </SessionWindow>
      </ScaledFrame>
    </MockScene>
  );
};

/** The call's last lines on the Transcript tab, until the call ends: Raj asks, and Dana's promise types in. */
const CallTranscript: Component<{ call: FollowThroughData["call"]; scene: Scene }> = (props) => {
  const typed = useTyping(() => props.call.promise.text, props.scene, CALL.typeFrom * 1000);
  return (
    <div class="exit col-start-1 row-start-1 px-5 py-4" style={at(CALL.end)}>
      <TranscriptBlock entry={props.call.said} />
      <TranscriptBlock entry={{ ...props.call.promise, text: typed() }} />
    </div>
  );
};

/** Activity while the call runs: Chief drafting the promised follow-up, then the draft saved in Gmail. */
const CallDraft: Component<{ call: FollowThroughData["call"] }> = (props) => (
  <div class="exit absolute inset-0 p-1.5" style={at(CALL.end)}>
    <div class="card-appear" style={at(CALL.drafting)}>
      <Swap
        class="grid-cols-[minmax(0,1fr)]"
        swapAt={CALL.email}
        before={<WorkingRow title={props.call.drafting} status={<TbBrandGmail class="size-3" aria-hidden="true" />} />}
        after={
          <div class="m-1 overflow-hidden rounded-md bg-tile text-sm ring-1 ring-primary-border">
            <p class="flex items-center gap-2 border-b border-primary-border px-2.5 py-2 font-semibold">
              <TbBrandGmail class="size-4 shrink-0 text-logo-gmail" aria-hidden="true" />
              <span class="truncate">{props.call.email.subject}</span>
            </p>
            <p class="px-2.5 py-2 text-xs leading-relaxed whitespace-pre-line text-secondary-text">
              {props.call.email.body}
            </p>
          </div>
        }
      />
      <p class="card-appear mx-1 flex items-center gap-2 p-1 text-xs text-muted-text" style={at(CALL.gmail)}>
        <TbBrandGmail class="size-3.5 shrink-0" aria-hidden="true" />
        {props.call.gmail}
        <span class={`${GHOST_BUTTON} ms-auto`}>{props.call.send}</span>
      </p>
    </div>
  </div>
);
